// 1. 判断平台
const userAgent = navigator?.userAgent?.toLowerCase() || ''
export const isAndroid = userAgent.includes('android')
export const isIOS = /iphone|ipad|ipod/.test(userAgent)
export const getPlatform = () => (isAndroid ? 'android' : isIOS ? 'ios' : 'web')

const BRIDGE_INITIALIZATION_TIMEOUT = 1500
const BRIDGE_CALL_TIMEOUT = 1500

import { getBridge } from './bridge'
import type { MapNavigationParams } from './brdegType'
import { shareWxMiniProgramParams, scanCodeParams, sharePhotoParams } from './brdegType'

declare global {
  interface Window {
    androidBridgeInited?: boolean
  }
}

function withTimeout<T>(promise: Promise<T>, timeout: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), timeout)

    promise.then(
      (result) => {
        clearTimeout(timer)
        resolve(result)
      },
      (error) => {
        clearTimeout(timer)
        reject(error)
      },
    )
  })
}



export const handleBridgePromise = async <T = any, R = any>(
  type: string,
  params: T,
): Promise<R> => {
  const bridge = await withTimeout(
    getBridge(),
    BRIDGE_INITIALIZATION_TIMEOUT,
    'Native bridge initialization timeout',
  )

  if (!bridge || typeof bridge.callHandler !== 'function') {
    throw new Error('Native bridge is unavailable')
  }

  return new Promise<R>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`Native bridge call timeout: ${type}`))
    }, BRIDGE_CALL_TIMEOUT)

    try {
      if (!window?.androidBridgeInited) {
        isAndroid && bridge.init()
        window.androidBridgeInited = true
      }
      console.log("调用原生方法:bridge.callHandler", type, params)
      bridge.callHandler(type, params, (data: any) => {
        // 检查返回的数据是否有错误
        console.log(type,'[Tabbar handleBridgePromise] 获取数据:', data)
        if (data && data.error) {
          clearTimeout(timer)
          reject(new Error(data.error))
          return
        }
        try {
          const result = isString(data) ? JSON.parse(data as string) : data
          clearTimeout(timer)
          resolve(result)
          if (type === 'routeTo' && (params as any).routeParameter?.callback) {
            (params as any).routeParameter.callback(result)
          }
        } catch (error) {
          clearTimeout(timer)
          resolve(data)
          if (type === 'routeTo' && (params as any).routeParameter?.callback) {
            (params as any).routeParameter.callback(data)
          }
        }
      })
    } catch (error) {
      clearTimeout(timer)
      reject(error)
    }
  })
}

const isString = (val: unknown): val is string => {
  return typeof val === 'string' || val instanceof String
}

// 桥接方法配置表
const bridgeMethods = [
  { name: 'userInfo', type: 'UserInfo', params: () => ({}) },
  { name: 'userLocation', type: 'getUserLocation', params: (p: any) => p },
  { name: 'getDeviceInfo', type: 'getDeviceInfo', params: () => ({}) },
  { name: 'mapContent', type: 'mapContent', params: (p: any) => p },
  { name: 'mapNavigation', type: 'mapNavigation', params: (p: MapNavigationParams) => p },
  { name: 'closeWebview', type: 'handleClose', params: (p: any) => p },
  { name: 'addCalendar', type: 'addCalendar', params: (p: any) => p },
  { name: 'saveAlbum', type: 'saveAlbum', params: (p: object) => p },
  { name: 'getAppInfo', type: 'getAppInfo', params: (p: object) => p },
  { name: 'openAlbum', type: 'openAlbum', params: (p: object) => p },
  { name: 'openAlbumV2', type: 'openAlbumV2', params: (p: object) => p },
  // showSoftInput
  { name: 'showSoftInput', type: 'showSoftInput', params: (p: any) => p },
  { name: 'joinOrExitTerritory', type: 'joinOrExitTerritory', params: (p: any) => p },
  { name: 'getChatHistory', type: 'getChatHistory', params: (p: any) => p },
  {
    name: 'webView',
    type: 'routeTo',
    params: (p: any) => ({
      routeName: 'webView',
      routeParameter: p,
    }),
  },
  {
    name: 'gotoLogin',
    type: 'routeTo',
    params: (p: any) => ({
      routeName: 'login',
      routeParameter: p,
    }),
  },
  {
    name: 'gotoLandPage',
    type: 'routeTo',
    params: (p: any) => ({
      routeName: 'LD://Territory/LDTerritoryVC',
      routeParameter: p,
    }),
  },
  {
    name: 'pushWxMiniProgram', // 跳转小程序
    type: 'routeTo',
    params: (p: any) => ({
      routeName: 'LD://Pay/PushWxMiniProgram',
      routeParameter: p,
    }),
  },
  {
    name: 'ApplePay', // 苹果充值
    type: 'routeTo',
    params: (p: any) => ({
      routeName: 'LD://Pay/ApplePay',
      routeParameter: p,
    }),
  },
  {
    name: 'scanCode', // 扫一扫
    type: 'routeTo',
    params: (p: scanCodeParams) => ({
      routeName: 'LD://swiftScan/LBXScanViewController',
      routeParameter: p,
    }),
  },
  {
    name: 'shareWxMiniProgram', // 分享小程序到微信好友
    type: 'routeTo',
    params: (p: shareWxMiniProgramParams) => ({
      routeName: 'LD://Share/WxMiniProgram',
      routeParameter: p,
    }),
  },
  {
    name: 'sharePhoto', // 分享图片
    type: 'routeTo',
    params: (p: sharePhotoParams) => ({
      routeName: 'LD://Share/Photo',
      routeParameter: p,
    }),
  },
  {
    name: 'publish', // 发布
    type: 'routeTo',
    params: (p: any) => ({
      routeName: 'LD://Publish/LDPublishVC',
      routeParameter: p,
    }),
  },
  {
    name: 'publishNormal', // 发布不带图片
    type: 'routeTo',
    params: (p: any) => ({
      routeName: 'LD://Connections/C/LDConnectionsPublishVC',
      routeParameter: p,
    }),
  },
  {
    name: 'resent', // 跳转web透明弹框
    type: 'routeTo',
    params: (p: any) => ({
      routeName: 'LD://Base/LDPresentWebVC',
      routeParameter: p,
    }),
  },
  {
    name: 'editUser', // 个人资料修改
    type: 'routeTo',
    params: (p: any) => ({
      routeName: 'LD://User/LDEditProfileVC',
      routeParameter: p,
    }),
  },
  {
    name: 'territory', // 领地
    type: 'routeTo',
    params: (p: any) => ({
      routeName: 'LD://Territory/LDTerritoryVC',
      routeParameter: p,
    }),
  },
  {
    name: 'shareCard', // 分享卡片
    type: 'routeTo',
    params: (p: any) => ({
      routeName: 'LD://Share/Card',
      routeParameter: p,
    }),
  },
  {
    name: 'share', // 分享链接
    type: 'routeTo',
    params: (p: any) => ({
      routeName: 'share',
      routeParameter: p,
    }),
  },
  {
    name: 'cart', // 购物车
    type: 'routeTo',
    params: (p: any) => ({
      routeName: 'cart',
      routeParameter: p,
    }),
  },
  {
    name: 'mall', // 商城首页
    type: 'routeTo',
    params: (p: any) => ({
      routeName: 'mall',
      routeParameter: p,
    }),
  },
  {
    name: 'couponwallet', // 券包
    type: 'routeTo',
    params: (p: any) => ({
      routeName: 'couponwallet',
      routeParameter: p,
    }),
  },
  {
    name: 'coupondetail', // 券详情
    type: 'routeTo',
    params: (p: any) => ({
      routeName: 'coupondetail',
      routeParameter: p,
    }),
  },
  {
    name: 'userHome', // 用户主页
    type: 'routeTo',
    params: (p: any) => ({
      routeName: 'LD://User/Userhome',
      routeParameter: p,
    }),
  },
  {
    name: 'selectAddress', // 选择位置页面
    type: 'routeTo',
    params: (p: any) => ({
      routeName: 'LD://Common/LDChooseLoctVC',
      routeParameter: p,
    }),
  },
  {
    name: 'messageView', // IM聊天
    type: 'routeTo',
    params: (p: any) => ({
      routeName: 'LD://EaseChatUIKit/MessageViewController',
      routeParameter: p,
    }),
  },
  {
    name: 'feedStream',
    type: 'routeTo',
    params: (p: any) => ({
      routeName: 'LD://Base/DY/DouyinViewController',
      routeParameter: p,
    }),
  },
  {
    name: 'toAppView', // 跳转app
    type: 'routeTo',
    params: (data: any) => ({
      routeName: data.routeName,
      routeParameter: data.routeParameter,
    }),
  },
]

// 自动注册LDBridge
export const LDBridge: Record<string, any> = {}
bridgeMethods.forEach((method) => {
  LDBridge[method.name] = (params?: any) => handleBridgePromise(method.type, method.params(params))
})
